import { SupportedLanguage } from '@foundation/localization';
import { useTranslation } from '@shared/localization';
import { PropsWithChildren, useMemo, JSX } from 'react';

import { BoldHeading, Heading, PageLayout } from '@/components/common';
import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import PaymentStore from '@/services/payments/PaymentStore';
import { formatPrice } from '@/utils/formatters';

import { OrderId } from '../text/OrderId';

interface PaymentsLayoutProps extends PropsWithChildren {
  readonly title?: string;
  readonly footer: JSX.Element | null;
  readonly navigation: JSX.Element | null;
  readonly mainAction: JSX.Element | null;
  readonly flow: PaymentIntentFlow;
}

const styles = {
  loadingSpinner: 'flex items-center justify-center',
  pageContent: 'p-sm md:p-md 2xl:p-lg !pt-0',
  pageContentHeader:
    'sticky top-0 p-sm md:py-0 md:px-md 2xl:px-lg z-50 text-black bg-secondary flex flex-col md:flex-row text-left md:items-center justify-between md:flex-wrap pt-8 md:pt-12 pb-4 md:pb-md',
  pageContentNavigation: 'flex',
  pageContentFooter: 'mt-md flex w-full items-center justify-center',
  pageStickyContainer:
    'sticky bottom-0 border-0 z-50 bg-transparent pb-xs pt-sm',
  pageInnerStickyContainer: 'flex justify-center',
  orderId:
    'text-gray-700 text-sm w-full flex justify-between md:w-auto items-center',
};

export function PaymentsLayout({
  navigation,
  title,
  footer,
  mainAction,
  children,
  flow,
}: PaymentsLayoutProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const headerText = useMemo(() => {
    if (flow === PaymentIntentFlow.Manage) {
      return t('Manage payment methods');
    }
    return t('Total: {{totalPrice}}', {
      totalPrice: formatPrice(
        language as SupportedLanguage,
        PaymentStore.currency,
        PaymentStore.orderTotal,
      ),
    });
  }, [flow, t, language]);

  return (
    <PageLayout>
      <div className={styles.pageContentHeader}>
        <BoldHeading extraClassName="text-xl md:text-2xl font-bold">
          {headerText}
        </BoldHeading>
        {flow !== PaymentIntentFlow.Manage ? (
          <OrderId
            extraClassName={styles.orderId}
            orderNumber={PaymentStore.orderNumber}
          />
        ) : (
          ''
        )}
      </div>

      <div className={styles.pageContent}>
        {title && (
          <div className={navigation ? 'mt-sm md:mt-md' : ''}>
            <Heading>{title}</Heading>
          </div>
        )}

        <div className="mt-xs">{children}</div>

        {mainAction && (
          <div className={styles.pageStickyContainer}>
            <div className={styles.pageInnerStickyContainer}>{mainAction}</div>
          </div>
        )}

        {footer && <div className={styles.pageContentFooter}>{footer}</div>}
      </div>
    </PageLayout>
  );
}
